// src/components/NewReservationAdmin.js
import React, { useState, useCallback, useEffect } from "react";
import useApi from "../../../Hooks/useApi";
import { ReservationSidebar } from "@happychef/reservation-sidebar";
import { AiOutlinePlus } from "react-icons/ai";
import "./css/newReservationAdmin.css";
import reservationSuccessSound from "../../../Assets/sounds/sound1.mp3";

const NewReservationAdmin = () => {
  const api = useApi();

  const initialFormData = {
    guests: 2,
    date: "",
    time: "",
    menu: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    extraInfo: "",
    personeel: "",
    giftcard: "",
    zitplaats: "",
    selectedTableNumbers: [],
    selectedTableIds: [],
    reservationMode: "met_limieten",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reservationSubmitted, setReservationSubmitted] = useState(false);

  // Listen for calendar + button click to open sidebar with pre-set date
  useEffect(() => {
    const handleOpenWithDate = (e) => {
      const { date } = e.detail;
      // Set global flag so ReservationStepOne can detect dashboard preset
      window.__reservationDashboardPreset = true;
      setFormData({
        ...initialFormData,
        guests: 2,
        date: date,
        reservationMode: "met_limieten",
      });
      setErrors({});
      setReservationSubmitted(false);
      setIsSidebarOpen(true);
    };
    window.addEventListener('openReservationWithDate', handleOpenWithDate);
    return () => window.removeEventListener('openReservationWithDate', handleOpenWithDate);
  }, []);

  // --- ensure arrays exist even if something upstream sets them to undefined/null
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      selectedTableNumbers: Array.isArray(prev.selectedTableNumbers)
        ? prev.selectedTableNumbers
        : [],
      selectedTableIds: Array.isArray(prev.selectedTableIds)
        ? prev.selectedTableIds
        : [],
    }));
  }, []);

  const clearTables = (next) => {
    next.selectedTableNumbers = [];
    next.selectedTableIds = [];
    return next;
  };

  const validateStepOne = () => {
    const e = {};
    if (!formData.guests) e.guests = "Aantal gasten is verplicht";
    if (!formData.date) e.date = "Datum is verplicht";
    if (!formData.time) e.time = "Tijd is verplicht";
    return e;
  };

  const validateStepTwo = () => {
    const e = {};
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email))
      e.email = "E-mail is ongeldig";

    // Enforce obligated fields from general settings
    const obligatedFields = window.generalSettings?.obligatedFields || [];
    if (obligatedFields.includes("firstName") && !formData.firstName?.trim()) {
      e.firstName = "Voornaam is verplicht";
    }
    if (obligatedFields.includes("lastName") && !formData.lastName?.trim()) {
      e.lastName = "Achternaam is verplicht";
    }
    if (obligatedFields.includes("email") && !formData.email?.trim()) {
      e.email = e.email || "E-mail is verplicht";
    }
    if (obligatedFields.includes("phone") && !formData.phone?.trim()) {
      e.phone = "Telefoon is verplicht";
    }

    return e;
  };

  const handleFinalSubmit = async (evt) => {
    evt.preventDefault();
    const allErrors = { ...validateStepOne(), ...validateStepTwo() };
    if (Object.keys(allErrors).length) return setErrors(allErrors);

    setErrors({});
    setIsSubmitting(true);

    const submissionData = {
      guests: formData.guests,
      date: formData.date,
      time: formData.time,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      extraInfoAdmin: formData.extraInfo,
      menu: formData.menu,
      personeel: formData.personeel,
      ...(formData.giftcard && { giftcard: formData.giftcard }),
      ...(formData.zitplaats && { zitplaats: formData.zitplaats }),
      reservationMode: formData.reservationMode,
      language: localStorage.getItem("preferredLanguage") || "nl",
    };

    if (
      formData.selectedTableNumbers?.length &&
      formData.selectedTableIds?.length
    ) {
      submissionData.tables = formData.selectedTableNumbers.map((n) =>
        parseInt(n, 10)
      );
      submissionData.tableIds = formData.selectedTableIds;
      console.log(
        "Submitting with selected tables:",
        submissionData.tables,
        submissionData.tableIds
      );
    } else {
      console.log("Submitting without pre-selected tables.");
    }

    try {
      console.log("Submitting Reservation Data:", submissionData);
      await api.post(
        `${window.baseDomain}api/auth-reservations/`,
        submissionData
      );

      // Play success sound with error handling for autoplay policies
      try {
        const audio = new Audio(reservationSuccessSound);
        await audio.play();
      } catch (playError) {
        // Browser blocked autoplay - user needs to interact with page first
        // This is expected behavior for autoplay policies, so we handle it gracefully
        if (playError.name === 'NotAllowedError') {
          console.log('Audio playback blocked - user interaction required first');
        } else {
          console.error('Error playing success sound:', playError);
        }
      }

      setReservationSubmitted(true);
    } catch (error) {
      console.error("Error submitting reservation:", error);
      setErrors({
        submit: "Kon reservering niet opslaan. Probeer het opnieuw.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = useCallback((e) => {
    // batched updates (your existing pattern)
    if (e.target.multiple && e.target.updates) {
      setFormData((prev) => {
        let next = { ...prev, ...e.target.updates };

        // normalize arrays in batch mode too
        if (!Array.isArray(next.selectedTableNumbers))
          next.selectedTableNumbers = [];
        if (!Array.isArray(next.selectedTableIds)) next.selectedTableIds = [];

        return next;
      });
      setErrors((prev) => {
        const next = { ...prev };
        Object.keys(e.target.updates).forEach((k) => delete next[k]);
        return next;
      });
      return;
    }

    const { name } = e.target;
    let value = e.target.value;

    // normalize some values
    if (name === "guests") value = parseInt(value, 10) || 1;
    if (name === "zitplaats") value = String(value || "");

    setFormData((prev) => {
      let next = { ...prev, [name]: value };

      // Whenever these change, we must recompute availability → clear dependent state
      if (
        name === "reservationMode" ||
        name === "giftcard" ||
        name === "guests" ||
        name === "zitplaats"
      ) {
        next.date = "";
        next.time = "";
        next = clearTables(next);
      }

      // Changing date clears time and tables
      if (name === "date") {
        next.time = "";
        next = clearTables(next);
      }

      // Changing time clears just tables (availability for tables is time-dependent)
      if (name === "time") {
        next = clearTables(next);
      }

      // keep arrays always arrays
      if (!Array.isArray(next.selectedTableNumbers))
        next.selectedTableNumbers = [];
      if (!Array.isArray(next.selectedTableIds)) next.selectedTableIds = [];

      return next;
    });

    // clear error for this field if present
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleCloseSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const handleNewReservation = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setReservationSubmitted(false);
  }, []);

  return (
    <div className="new-reservation-page">
      <button
        className="open-sidebar-button"
        data-tutorial-target="new-reservation-button"
        onClick={() => setIsSidebarOpen(true)}
        style={{
          zIndex: isSidebarOpen ? -1 : undefined,
          opacity: isSidebarOpen ? 0 : 1,
        }}
        aria-label="Nieuwe reservering"
      >
        <AiOutlinePlus size={24} color="#fff" />
      </button>

      <ReservationSidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        handleFinalSubmit={handleFinalSubmit}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        reservationSubmitted={reservationSubmitted}
        onNewReservation={handleNewReservation}
        isAdmin={true}
      />
    </div>
  );
};

export default NewReservationAdmin;
